import type { Contact } from "@/features/contact/types";
import type {
  Access,
  ContactVisibility,
  CreateRoleInput,
  Role,
  UpdateRoleInput,
} from "@/types/role";
import type { WorkspaceRole } from "@/types/user";

import { useMockStore } from "../store";
import { createId } from "../generators/base";
import { MOCK_PERMISSION_RULES as RULES } from "../permission-catalog";

import { getWorkspaceUsersMock } from "./user";

import { isContactVisible } from "@/mock/helpers/contact-wall";
import { canLimitContacts } from "@/features/roles/helpers/roles";
import { useGlobalStore } from "@/stores/global";
import { CONTACT_VISIBILITY } from "@/types/role";
import { ORG_ROLE, WORKSPACE_ROLE } from "@/types/user";
import { getUserTeams } from "@/utils/team";

type StoredRole = Omit<Role, "memberCount">;

const activeWorkspaceId = () =>
  useGlobalStore.getState().activeWorkspaceId ??
  useMockStore.getState().workspaces[0]?.id;

const SYSTEM_ROLES: StoredRole[] = [
  {
    id: WORKSPACE_ROLE.ADMIN,
    name: "Admin",
    description: "Sees and does everything, including roles and members",
    base: WORKSPACE_ROLE.ADMIN,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.preset(WORKSPACE_ROLE.ADMIN),
    isSystem: true,
  },
  {
    id: WORKSPACE_ROLE.MEMBER,
    name: "Member",
    description: "Runs the day to day, without workspace administration",
    base: WORKSPACE_ROLE.MEMBER,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.preset(WORKSPACE_ROLE.MEMBER),
    isSystem: true,
  },
  {
    id: WORKSPACE_ROLE.AGENT,
    name: "Agent",
    description: "Works conversations and the contacts behind them",
    base: WORKSPACE_ROLE.AGENT,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.preset(WORKSPACE_ROLE.AGENT),
    isSystem: true,
  },
  {
    id: WORKSPACE_ROLE.MARKETING,
    name: "Marketing",
    description:
      "Runs campaigns, workflows and ads, and reads the reports without visit or sales value",
    base: WORKSPACE_ROLE.MARKETING,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.preset(WORKSPACE_ROLE.MARKETING),
    isSystem: true,
  },
  {
    id: WORKSPACE_ROLE.FINANCE,
    name: "Finance",
    description:
      "Sees everything, amounts and billing included, without channels or setup",
    base: WORKSPACE_ROLE.FINANCE,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.preset(WORKSPACE_ROLE.FINANCE),
    isSystem: true,
  },
];

// Stable ids so the editor has fixed mock URLs (/settings/roles/front-desk).
// The backend uses uuids.
const seedCustomRoles = (): StoredRole[] => [
  {
    id: "front-desk",
    name: "Front desk",
    description: "Books and moves appointments for their teams' patients",
    base: WORKSPACE_ROLE.AGENT,
    contactVisibility: CONTACT_VISIBILITY.TEAMS,
    permissions: RULES.normalize(
      RULES.tick(RULES.preset(WORKSPACE_ROLE.AGENT), "appointments.delete"),
      WORKSPACE_ROLE.AGENT,
      CONTACT_VISIBILITY.TEAMS,
    ),
    isSystem: false,
  },
  {
    id: "outsourced-agent",
    name: "Outsourced agent",
    description: "Sees only the contacts handed to them, exports nothing",
    base: WORKSPACE_ROLE.AGENT,
    contactVisibility: CONTACT_VISIBILITY.ASSIGNED,
    permissions: RULES.normalize(
      RULES.untick(
        RULES.untick(RULES.preset(WORKSPACE_ROLE.AGENT), "replies.manage"),
        "conversations.assign",
      ),
      WORKSPACE_ROLE.AGENT,
    ),
    isSystem: false,
  },
  {
    id: "clinic-manager",
    name: "Clinic manager",
    description: "A member who also sees revenue and the billing ledger",
    base: WORKSPACE_ROLE.MEMBER,
    contactVisibility: CONTACT_VISIBILITY.ALL,
    permissions: RULES.normalize(
      RULES.tick(
        RULES.tick(RULES.preset(WORKSPACE_ROLE.MEMBER), "reports.amount"),
        "billing.view",
      ),
      WORKSPACE_ROLE.MEMBER,
    ),
    isSystem: false,
  },
];

let roles: StoredRole[] | null = null;

// Seeds once per page load and hands two agents the custom roles, so the
// member counts and the member modal have something to show.
const getStoredRoles = (): StoredRole[] => {
  if (roles) return roles;

  const custom = seedCustomRoles();
  const workspaceId = activeWorkspaceId();
  const store = useMockStore.getState();
  const agents = getWorkspaceUsersMock(workspaceId).filter(
    (user) =>
      user.role === WORKSPACE_ROLE.AGENT && user.id !== store.currentUserId,
  );

  agents.slice(0, 2).forEach((agent, index) => {
    const user = store.users.find((item) => item.id === agent.id);

    if (!user) return;
    store.updateUser(user.id, {
      workspaces: user.workspaces.map((workspace) =>
        workspace.id === workspaceId
          ? { ...workspace, roleId: custom[index].id }
          : workspace,
      ),
    });
  });

  roles = [...SYSTEM_ROLES, ...custom];

  return roles;
};

// A built-in role's id is its base value, so a member without a roleId holds
// the built-in for their base.
// ponytail: one role list for every mock workspace; the backend keeps one per workspace.
const withMemberCount = (
  role: StoredRole,
  workspaceId = activeWorkspaceId(),
): Role => ({
  ...role,
  memberCount: getWorkspaceUsersMock(workspaceId).filter(
    (member) => (member.roleId ?? member.role) === role.id,
  ).length,
});

const assertNameFree = (name: string, exceptId?: string) => {
  if (!name.trim()) throw new Error("Give the role a name");
  const taken = getStoredRoles().some(
    (role) =>
      role.id !== exceptId &&
      role.name.toLowerCase() === name.trim().toLowerCase(),
  );

  if (taken) throw new Error(`A role called "${name.trim()}" already exists`);
};

const assertMayLimitContacts = (
  base: WorkspaceRole,
  contactVisibility: ContactVisibility,
) => {
  if (!canLimitContacts(base) && contactVisibility !== CONTACT_VISIBILITY.ALL)
    throw new Error(
      "Only a role for members or agents can limit which contacts it sees",
    );
};

export function getRolesMock(workspaceId?: string): Role[] {
  return getStoredRoles().map((role) => withMemberCount(role, workspaceId));
}

export function createRoleMock(input: CreateRoleInput): Role {
  assertNameFree(input.name);
  assertMayLimitContacts(input.base, input.contactVisibility);
  const role: StoredRole = {
    ...input,
    id: createId(),
    name: input.name.trim(),
    permissions: RULES.normalize(
      input.permissions,
      input.base,
      input.contactVisibility,
    ),
    isSystem: false,
  };

  roles = [...getStoredRoles(), role];

  return withMemberCount(role);
}

export function updateRoleMock(id: string, input: UpdateRoleInput): Role {
  const role = getStoredRoles().find((item) => item.id === id);

  if (!role) throw new Error("Role not found");
  if (role.base === WORKSPACE_ROLE.ADMIN && role.isSystem)
    throw new Error("The Admin role can't be changed");
  if (
    role.isSystem &&
    (input.name !== undefined || input.description !== undefined)
  )
    throw new Error("Member and Agent keep their name and description");
  if (input.name !== undefined) assertNameFree(input.name, id);
  assertMayLimitContacts(role.base, input.contactVisibility);

  const updated: StoredRole = {
    ...role,
    name: input.name?.trim() ?? role.name,
    description:
      input.description === undefined ? role.description : input.description,
    contactVisibility: input.contactVisibility,
    permissions: RULES.normalize(
      input.permissions,
      role.base,
      input.contactVisibility,
    ),
  };

  roles = getStoredRoles().map((item) => (item.id === id ? updated : item));

  return withMemberCount(updated);
}

export function deleteRoleMock(id: string): void {
  const role = getRolesMock().find((item) => item.id === id);

  if (!role) throw new Error("Role not found");
  if (role.isSystem) throw new Error("Built-in roles can't be deleted");
  if (role.memberCount > 0)
    throw new Error("Move its members to another role first");
  roles = getStoredRoles().filter((item) => item.id !== id);
}

// What `/users/me` sends as `access` for the signed-in mock user in the active
// workspace. Org admins hold everything, like on the backend.
export function getMeAccessMock(): Access | null {
  const storedRoles = getStoredRoles();
  const { currentUserId, teams } = useMockStore.getState();
  const member = getWorkspaceUsersMock(activeWorkspaceId()).find(
    (user) => user.id === currentUserId,
  );

  if (!member) return null;
  const role = storedRoles.find(
    (item) => item.id === (member.roleId ?? member.role),
  );
  const isOrgAdmin = member.organizationRole === ORG_ROLE.ADMIN;
  const contactVisibility = isOrgAdmin
    ? CONTACT_VISIBILITY.ALL
    : (role?.contactVisibility ?? CONTACT_VISIBILITY.ALL);

  return {
    roleId: role?.id ?? null,
    roleName: role?.name ?? null,
    base: member.role,
    organizationRole: member.organizationRole,
    permissions: isOrgAdmin
      ? [...RULES.keys]
      : RULES.normalize(
          role?.permissions ?? [],
          member.role,
          contactVisibility,
        ),
    contactVisibility,
    teamIds: getUserTeams(teams, member.id).map((team) => team.id),
  };
}

// The contact wall for the signed-in mock user, as the backend applies it to
// every contact read.
export function seesContactMock(
  contact: Pick<Contact, "assignedUserId" | "assignedTeamId">,
): boolean {
  const access = getMeAccessMock();

  return isContactVisible(contact, {
    userId: useMockStore.getState().currentUserId,
    contactVisibility: access?.contactVisibility ?? CONTACT_VISIBILITY.ALL,
    teamIds: access?.teamIds ?? [],
  });
}

const toSlug = (name: string) => name.trim().toLowerCase().replace(/\s+/g, "-");

// Hands the mock user the role named by `?mockAs=` in every workspace.
export function applyMockAsRole(roleName: string, userId: string): void {
  const role = getStoredRoles().find(
    (item) => toSlug(item.name) === toSlug(roleName),
  );
  const store = useMockStore.getState();
  const user = store.users.find((item) => item.id === userId);

  if (!role || !user) return;
  store.updateUser(user.id, {
    workspaces: user.workspaces.map((workspace) => ({
      ...workspace,
      role: role.base,
      roleId: role.id,
    })),
  });
}
